import type {Month} from "date-fns";
import {
    addDays,
    format,
    formatDistanceToNow as formatDistanceToNowFns,
    isSameDay,
    isSameMonth,
    isToday,
    isTomorrow,
    isWithinInterval,
    isYesterday,
    parseISO,
    subDays
} from "date-fns";
import {enUS} from "date-fns/locale/en-US";

import {fromLocalToZoned} from "./timezone";

export function formatDateRange(
    startDate: Date | string,
    endDate: Date | string,
    timezone: string
) {
    const startDateTz = fromLocalToZoned(startDate, timezone);
    const endDateTz = fromLocalToZoned(endDate, timezone);

    let reservationDateRange = "";
    if (format(startDateTz, "MMM") === format(endDateTz, "MMM")) {
        reservationDateRange = `${format(startDateTz, "MMM d")} - ${format(endDateTz, "d, y")}`;
    } else {
        reservationDateRange = `${format(startDateTz, "MMM d")} - ${format(endDateTz, "MMM d, y")}`;
    }

    return reservationDateRange;
}

export const formatDate = (date: string | Date, formatPattern: string) => {
    if (typeof date === "string") {
        return format(new Date(date), formatPattern);
    }
    return format(date, formatPattern);
};

export function formatyyyyMMdd(date: Date | string) {
    const formatPattern = "yyyy-MM-dd";
    return formatDate(date, formatPattern);
}

export function buildSelectedDatesFormatted(startDate: Date, endDate: Date) {
    let selectedDatesFormatted = "";
    if (endDate && !isSameDay(startDate, endDate)) {
        selectedDatesFormatted = format(startDate, "MMM, do");
        if (isSameMonth(startDate, endDate)) {
            selectedDatesFormatted += ` - ${format(endDate, "do yyyy")}`;
        } else {
            selectedDatesFormatted += ` - ${format(endDate, "MMM, do yyyy")}`;
        }
    } else {
        selectedDatesFormatted = format(startDate, "MMM, do yyyy");
    }
    return selectedDatesFormatted;
}

export function formatDistanceFromNow(timestamp: Date) {
    let dateString = format(timestamp, "eee, MMMM do yyyy");
    if (isToday(timestamp)) {
        dateString = "Today";
    } else if (isTomorrow(timestamp)) {
        dateString = "Tomorrow";
    } else if (
        isWithinInterval(timestamp, {
            start: new Date(),
            end: addDays(new Date(), 7)
        })
    ) {
        dateString = format(timestamp, "eeee");
    } else if (isYesterday(timestamp)) {
        dateString = "Yesterday";
    } else if (
        isWithinInterval(timestamp, {
            start: subDays(new Date(), 7),
            end: new Date()
        })
    ) {
        dateString = `Last ${format(timestamp, "eeee")}`;
    }
    return dateString;
}

export function formatDistanceToNow(date: string | number | Date, includeSeconds?: boolean) {
    return formatDistanceToNowFns(typeof date === "string" ? parseISO(date) : new Date(date), {
        includeSeconds,
        addSuffix: true
    });
}

export const getMonthName = (date: Date) => enUS.localize?.month(date.getMonth() as Month);

export function generateHours() {
    const opts: {value: number; label: string}[] = [];
    // Loop through 12 hours
    for (let hour = 1; hour < 13; hour += 1) {
        opts.push({
            value: hour,
            label: hour.toString()
        });
    }
    return opts;
}

export function formatMMddyyyy(date: Date | string) {
    const formatPattern = "MM/dd/yyyy";
    return formatDate(date, formatPattern);
}
